import * as React from "react";
import AnalyticsEngine from "../data/AnalyticsEngine";
import DashboardDateUtil from "./DashboardDateUtil";
import {ArrayUtil} from "oca-lib-web";
import GoogleAnalyticsDataAccess from "../data/GoogleAnalyticsDataAccess";
import AppStoreConnectDataAccess from "../data/AppStoreConnectDataAccess";
import FlurryDataAccess from "../data/FlurryDataAccess";
import Constants from "./Constants";
import EventUtil from "./EventUtil";
import GraphFactory from "./GraphFactory";
import DatasetUtil from "./DatasetUtil";
import TwitterDataAccess from "../data/TwitterDataAccess";

export default class GraphUtil {

    public static getMetricOptions(item:GraphSettings):Array<string> {
        let analyticType = item.dataSource;
        if (analyticType === AppStoreConnectDataAccess.APPDOWNLOADS) {
            return AppStoreConnectDataAccess.getMetricOptions();
        } else if (analyticType === FlurryDataAccess.APPUSAGE) {
            return [FlurryDataAccess.APPUSAGE_SESSIONS, FlurryDataAccess.APPUSAGE_ACTIVEDEVICES, FlurryDataAccess.APPUSAGE_ACTIVEUSERS,
                FlurryDataAccess.APPUSAGE_NEWDEVICES, FlurryDataAccess.APPUSAGE_TIMESPENT, FlurryDataAccess.APPUSAGE_AVGTIMEPERDEVICE,
                FlurryDataAccess.APPUSAGE_AVGTIMEPERSESSION, FlurryDataAccess.APPUSAGE_MEDIANTIMEPERSESSION];
        } else if (analyticType === FlurryDataAccess.EVENTPARAMS) {
            return [Constants.METRIC_COUNT];
        } else if (analyticType === GoogleAnalyticsDataAccess.USERSESSIONS) {
            return [GoogleAnalyticsDataAccess.USERSESSIONS_USERS, GoogleAnalyticsDataAccess.USERSESSIONS_NEWUSERS, GoogleAnalyticsDataAccess.USERSESSIONS_SESSIONS,
                GoogleAnalyticsDataAccess.USERSESSIONS_BOUNCES, GoogleAnalyticsDataAccess.USERSESSIONS_BOUNCERATE, GoogleAnalyticsDataAccess.USERSESSIONS_SESSIONDURATION,
                GoogleAnalyticsDataAccess.USERSESSIONS_AVGSESSIONDURATION];
        } else if (analyticType === GoogleAnalyticsDataAccess.TRAFFICSOURCES) {
            return [GoogleAnalyticsDataAccess.TRAFFICSOURCES_VISITS];
        } else if (analyticType === GoogleAnalyticsDataAccess.PAGEVIEWS) {
            return [GoogleAnalyticsDataAccess.PAGEVIEWS_PAGEVIEWS, GoogleAnalyticsDataAccess.PAGEVIEWS_UNIQUEPAGEVIEWS,
                GoogleAnalyticsDataAccess.PAGEVIEWS_AVGTIMEONPAGE]
        } else if (analyticType === TwitterDataAccess.TWITTER) {
            return TwitterDataAccess.getMetricOptions();

        } else {
            let data:Array<any> = AnalyticsEngine.getData(item, DashboardDateUtil.getTimeInterval());
            console.log(data);
            if (data?.length > 0) {
                return Object.keys(data[0]);
            }
        }
        return [];
    }

    public static getXAxisOptions(item:GraphSettings):Array<string> {
        let analyticType = item.dataSource;
        if (analyticType == AppStoreConnectDataAccess.APPDOWNLOADS) {
            return AppStoreConnectDataAccess.getXAxisOptions();
        } else if (analyticType == FlurryDataAccess.APPUSAGE) {
            return [Constants.AXIS_DATE, "appName"];
        } else if (analyticType == FlurryDataAccess.EVENTPARAMS) {
            let params = EventUtil.getParamNames(item);
            return [Constants.AXIS_DATE, "appName", "eventName", ...params];
        } else if (analyticType == GoogleAnalyticsDataAccess.USERSESSIONS) {
            return [Constants.AXIS_DATE];
        } else if (analyticType == GoogleAnalyticsDataAccess.TRAFFICSOURCES) {
            return [Constants.AXIS_DATE, GoogleAnalyticsDataAccess.TRAFFICSOURCES_MEDIUM, GoogleAnalyticsDataAccess.TRAFFICSOURCES_SOURCE];
        } else if (analyticType == GoogleAnalyticsDataAccess.PAGEVIEWS) {
            return [Constants.AXIS_DATE, GoogleAnalyticsDataAccess.PAGEVIEWS_PAGEPATH];
        } else if (analyticType == TwitterDataAccess.TWITTER) {
            return TwitterDataAccess.getXAxisOptions();
        } else {
            let data:Array<any> = AnalyticsEngine.getData(item, DashboardDateUtil.getTimeInterval());
            console.log(data);
            if (data?.length > 0) {
                return Object.keys(data[0]);
            }
        }
        return [];
    }

    public static isChart(chartType:string):boolean {
        return chartType == Constants.CHART_TYPE_BAR || chartType == Constants.CHART_TYPE_DONUT || chartType == Constants.CHART_TYPE_GEO ||
            chartType == Constants.CHART_TYPE_LINE || chartType == Constants.CHART_TYPE_WORDCLOUD;
    }

    public static getGraph(graphSettings:GraphSettings, dataset:any, timeInterval:TimeInterval, color:string, onRowSelection:any) {
        let fullDataset = DatasetUtil.generateChartDataset(graphSettings, dataset, timeInterval, color);
        if (!fullDataset) {
            console.error("Could not find dataset for: " + graphSettings.dataSource);
        }
        if (!dataset || dataset.length == 0) return <div>Cant find dataset</div>;

        return GraphFactory.create(graphSettings, fullDataset, onRowSelection);
    }

    public static getSummaryStats(item:GraphSettings, dataset:any, timeInterval:TimeInterval, color:string) {
        //let dataset = this.generateTimeDataset(item, timeInterval, color);
        //console.log(dataset);
        let aggregate = this.aggregateDataset(item, dataset);
        let stats = this.generateStats(aggregate, timeInterval, color);
        return (
            stats ? stats.map((item:any, index:any) => (
                <React.Fragment key={item.graphName + "_" + index}>
                    {item.label} <span className={`units ${item.color}`}>{item.value}</span>
                    {(index < stats.length - 1) &&
                    <span className="divider"> / </span>}
                </React.Fragment>
            )) : null
        )
    }

    public static generateStats(sumValue:number, timeInterval:TimeInterval, color:string):Array<any> {
        return [
            {value: Math.ceil(sumValue / 30), label: 'Daily', color: color},
            {value: Math.ceil(sumValue / 4), label: 'Weekly', color: color},
            {value: sumValue, label: 'Monthly', color: color},
        ]
    }

    public static shouldShowStats(item:GraphSettings): boolean {
        if (item.chartType == Constants.CHART_TYPE_GEO) {
            return false;
        } else if (item.chartType == Constants.CHART_TYPE_LIST) {
            return false;
        }
        return true;
    }

    public static getTextGraphs(analyticsPage:PageSettings):Array<GraphSettings> {
        return ArrayUtil.getItems(analyticsPage.graphs, "chartType", Constants.CHART_TYPE_TEXT);
    }

    public static getSummaryGraphs(analyticsPage:PageSettings):Array<GraphSettings> {
        return ArrayUtil.getItems(analyticsPage.graphs, "chartType", Constants.CHART_TYPE_SUMMARY);
    }

    public static getDetailGraphs(analyticsPage:PageSettings):Array<GraphSettings> {
        let detailGraphs = [];
        for (let graph of analyticsPage.graphs) {
            if (this.isChart(graph.chartType) || graph.chartType == Constants.CHART_TYPE_LIST) {
                detailGraphs.push(graph);
            }
        }
        return detailGraphs;
        //return ArrayUtils.getItems(analyticsPage.graphs, "chartType", Constants.CHART_TYPE_SUMMARY, true);
    }

    public static getChartTypeDescription(chartType:string):string {
        for (let chartTypeItem of Constants.chartTypes) {
            if (chartTypeItem["value"] == chartType) {
                return chartTypeItem["label"];
            }
        }
        return "";
    }


    /*public static getAnalyticsPage(pageName:String, analyticsSettings:ReportSettings) {
        if (!analyticsSettings?.pages) return null;

        for (let page of analyticsSettings.pages) {
            if (page.id == pageName) {
                return page;
            }
        }
        return null;
    }*/

    /*public static async updateReportJson(reportJson:object, project:ProjectModel):Promise<ProjectModel> {
        project.reportJson = JSON.stringify(reportJson);
        await getGlobal().dataManager.save("createProject", project);
        project.reportJson = reportJson;
        return P.resolve(project);
    }*/



    public static getGraphKey(item:GraphSettings, type:string): string { return item.id + "_" + type; }

    public static aggregate(item:GraphSettings, dataset:Array<any>): number {
        let value = 0;
        if (!dataset) { return value }

        for (let dataItem of dataset) {
            value += dataItem[item.metric];
            if (item.dataSource == AppStoreConnectDataAccess.APPDOWNLOADS) {
                //console.log(dataItem[item.metric]);
            }
        }
        return value;
    }

    public static aggregateDataset(item:GraphSettings, dataset:Array<any>): number {
        //console.log("AGGREGATE DATASET")
        let value = 0;
        if (!dataset) { return value }
        //console.log(dataset);
        for (let dataItem of dataset) {
            value += dataItem[item.metric];
        }
        return value;
    }

    public static calculatePercentChange(item:GraphSettings, currentDataset:Array<any>, previousDataset:Array<any>): number {
        let currentAggregate = this.aggregate(item, currentDataset);
        let previousAggregate = this.aggregate(item, previousDataset);
        return (currentAggregate - previousAggregate) / currentAggregate;
    }
}